import { toSameOriginPath } from 'Utilities'

import {
  type Auth0Connection,
  Auth0ConnectionProvider,
  type Auth0Env,
  type Auth0Params,
} from 'Portal/src/utilities/auth0/auth0.types'

/**
 * Resolve a post-login redirect target to a safe, same-origin destination.
 *
 * The raw value comes from the user-controlled `redirect-to` query param, so it
 * is parsed against the current origin and only returned when it stays on the
 * same origin AND uses an http(s) protocol. Anything cross-origin, a
 * `javascript:`/`data:` scheme, or a malformed value falls back to '/', which
 * prevents open-redirect phishing.
 */
export const getSafeRedirectUrl = (
  rawRedirect: string,
  origin: string = window.location.origin,
): string => {
  const safePath = toSameOriginPath(rawRedirect, origin)

  return safePath ? `${origin}${safePath}` : '/'
}

const LOCAL_PATH = /^\/(?![/\\])/
const REDIRECT_PAGE = /^\/redirect(?:[?#]|$)/

/**
 * The server sends any page that needed a login as `redirect-to`. API endpoints answer with
 * data and /redirect would send the user back to itself, so neither is reopened after the login.
 */
export const isServerPageTarget = (target: string | null): target is string =>
  target !== null &&
  LOCAL_PATH.test(target) &&
  !target.startsWith('/api/') &&
  !REDIRECT_PAGE.test(target)

/**
 * The page to open after an SSO login, as a same-origin path. Same precedence as the
 * password login: a page target from the server wins over a portal route target.
 */
export const getPostLoginRedirectTarget = (
  {
    origin,
    pathname,
    search,
  }: Pick<Location, 'origin' | 'pathname' | 'search'>,
  routeSearch: string,
): string | null => {
  const serverRedirect = new URLSearchParams(search).get('redirect-to')
  const routeRedirect = new URLSearchParams(routeSearch).get('redirect-to')
  const rawRedirect = isServerPageTarget(serverRedirect)
    ? serverRedirect
    : routeRedirect

  return rawRedirect
    ? toSameOriginPath(rawRedirect, `${origin}${pathname}`)
    : null
}

/**
 * Builds the Auth0 authorize URL; /auth/sso opens the page from `state` after the login.
 */
export const generateAuth0Url = (
  connection: Auth0Connection,
  email: string,
  redirectTarget: string | null,
): string => {
  const auth0Env = connection.auth0Env[window.APP_ENV as keyof Auth0Env]
  const params = new URLSearchParams({
    client_id: auth0Env.clientId,
    connection: connection.name,
    redirect_uri: `${window.origin}/auth/sso`,
    response_mode: 'form_post',
    response_type: 'token',
    scope: 'openid email',
  } satisfies Auth0Params)

  if (connection.provider === Auth0ConnectionProvider.Google) {
    // have the ui default to the email typed in
    params.append('login_hint', email)
  }

  if (redirectTarget) {
    params.append('state', redirectTarget)
  }

  return `${auth0Env.domain}/authorize?${params.toString()}`
}
